<script setup lang="ts">
import { signInWithCustomToken } from 'firebase/auth';
import { cleanupFirebaseAuthObserver, firebaseAuthObserver, setCustomTokenSignInFlag } from '~/controllers/authentication';
import { fb_auth } from '~/services/firebase';

const { loading = true } = defineProps<{
  loading?: boolean
}>()
const { startDriver } = useDriver()
const { getCurrentUser } = useAuthStore()
const { resetFilters } = useCollectsStore()
const {
  user, isAdmin, isDriver,
} = storeToRefs(useAuthStore())

const route = useRoute()
const router = useRouter()
const tokenQuery = computed(() => route.query.token as string)

// Get token from location object as fallback when query is undefined
const getTokenFromLocation = () => {
  const urlParams = new URLSearchParams(location.search)
  return urlParams.get('token')
}

const path = computed(() => location.pathname)
const userloading = ref<boolean>(false)
// Add debounce to prevent flickering
const showLoading = ref<boolean>(true)
const isLoading = computed(() => loading || userloading.value)

// Watch for loading state changes with debounce to prevent flickering
watch(isLoading, (newLoading) => {
  if (newLoading) {
    // Immediately show loading when loading starts
    showLoading.value = true
  }
  else {
    // Delay hiding loading by a small amount to prevent flickering
    setTimeout(() => {
      // Only hide if still not loading after timeout
      if (!isLoading.value)
        showLoading.value = false
    }, 300)
  }
}, { immediate: true })

watch(() => authState.state, async () => {
  switch (authState.state) {
    case AuthStatus.SIGN_UP_SUCCESS:
      handleSignupSuccess()
      break
    case AuthStatus.SIGN_IN_SUCCESS:
      handleSigninSuccess()
      break
    case AuthStatus.SIGNED_OUT:
      handleSignout()
      break
  }
}, { immediate: true, deep: true })

async function handleSigninSuccess() {
  // Skip authentication handling for following routes
  // if (path.value === '/auth/user-invitation' || path.value.includes('/confirm-collect'))
  //   return

  // Set loading state immediately to prevent flickering
  userloading.value = true

  try {
    await getCurrentUser()
    await redirectToPath()

    // Start driver for non-admin users who accepted terms
    setTimeout(() => {
      if (!isAdmin.value && user.value?.user?.acceptedTermsAt !== null) {
        startDriver()
      }
    }, 500)
  }
  finally {
    // Only turn off loading after everything is complete
    userloading.value = false
  }
}

async function handleSignupSuccess() {
  // Set loading state immediately to prevent flickering
  userloading.value = true

  try {
    await getCurrentUser()
    // Check if we have a specific onboarding path to redirect to
    if (authState.onboardingRedirectPath) {
      const redirectPath = authState.onboardingRedirectPath
      // Reset the path immediately to prevent duplicate redirects
      authState.onboardingRedirectPath = null
      // Navigate to the specified path
      await router.push(redirectPath)
    }
    else {
      await redirectToPath()
    }
  }
  finally {
    // Only turn off loading after everything is complete
    userloading.value = false
  }
}

async function redirectToPath() {
  const queryParams = new URLSearchParams(window.location.search)
  const queryObject = Object.fromEntries(queryParams.entries())

  // Check if we're on the create account page and the state is SSO_SIGN_IN
  // In this case, we want to redirect to home since it's an existing user signing in
  const isCreateAccountPage = path.value.includes('/auth/create-an-account')
  const isLoginPage = path.value === '/auth/login'
  const shouldRedirectToHome = isLoginPage || (isCreateAccountPage && authState.state === AuthStatus.SSO_SIGN_IN)

  let targetPath = shouldRedirectToHome ? '/' : path.value

  // If the user is a driver, redirect to the driver dashboard
  if (isDriver.value) {
    targetPath = '/missions'
  }

  // Preserve the current query parameters
  await router.push({
    path: targetPath,
    query: shouldRedirectToHome ? {} : queryObject,
  })
}

async function handleSignout() {
  if (resetFilters)
    resetFilters()
  // @ts-expect-error user is of type CurrentUserGraphql
  user.value = undefined
  token.value = ''
  beforeAuthChangedUser.value = undefined
  useFakeData.value = false
  authState.firebaseUserPopulated = false
  authState.provider = 'firebase'
  authState.initialized = false
}

// Handle token sign-in from email links
const handleTokenSignIn = async (tokenToSignIn: string) => {
  if (!tokenToSignIn) return

  try {
    console.log('Signing in with custom token from URL')

    // Set custom token sign-in flag to prevent interference
    setCustomTokenSignInFlag(true)

    // Set auth state to prevent interference from firebaseAuthObserver
    authState.state = AuthStatus.SIGN_IN

    // Temporarily disable auth observers to prevent race conditions
    cleanupFirebaseAuthObserver()

    // @ts-expect-error _tokenResponse exist in user
    const { _tokenResponse } = await signInWithCustomToken(fb_auth, tokenToSignIn)
    token.value = _tokenResponse.idToken

    // Set auth state to success before getting user data
    authState.state = AuthStatus.SIGN_IN_SUCCESS

    await getCurrentUser()

    // Clear custom token sign-in flag
    setCustomTokenSignInFlag(false)

    // Re-enable auth observers after successful custom token sign-in
    await firebaseAuthObserver()

    console.log('Successfully signed in with custom token')
  } catch (error) {
    console.error('Error signing in with custom token:', error)
    // Clear custom token sign-in flag on error
    setCustomTokenSignInFlag(false)
    // Reset auth state on error
    authState.state = AuthStatus.SIGNED_OUT
    // Re-enable auth observers even on error
    await firebaseAuthObserver()
  }
}

// Check for token in URL on mount (for email links)
onMounted(async () => {

  // Try route query first, then fallback to location
  const token = tokenQuery.value || getTokenFromLocation()
  if (token) {
    await handleTokenSignIn(token)
  }
})
</script>

<template>
  <div v-if="!showLoading" class="h-full w-full flex flex-col">
    <slot />
  </div>

  <!-- Responsive loading based on screen size -->
  <div v-else class="h-full w-full">
    <!-- Mobile: Use simple loader or mobile skeleton -->
    <div class="md:hidden h-full w-full flex flex-col items-center justify-center">
      <MobileAppSkeleton />
    </div>

    <!-- Desktop: Use full app skeleton -->
    <div class="hidden md:block h-full w-full">
      <AppSkeleton />
    </div>
  </div>
</template>
